(ns manatee.session.handlers
  (:require [#?(:clj clj-time.core :cljs cljs-time.core) :as t]
            [ajax.edn :refer [edn-request-format edn-response-format]]
            [bedrock-ui.sentry.handlers :as sentry]
            [clojure.math :as math]
            [kee-frame.core :as kf]
            [manatee.auth.token :as token]
            [manatee.session.app-db :as session-db]
            [manatee.user.app-db :as user-db]
            [re-frame.core :as rf]
            [taoensso.timbre :as log]))

;;; Lower-level

(defn- ->millis-since-epoch
  "The number of milliseconds in the interval between the epoch and `date-time`."
  [date-time]
  (t/in-millis (t/interval (t/epoch)
                           date-time)))

(defn- backoff-millis
  "Calculate a number of milliseconds using the current failure count."
  [db]
  (* (math/pow 2.0
               (dec (session-db/post-events-failure-count db)))
     1000))

(rf/reg-event-fx
 ::post-events
 (fn post-events
   [{:keys [db]} [_ {:keys [events] :as request} is-retry?]]
   (let [now-ts (->millis-since-epoch (t/now))]
     {:db (session-db/set-last-events-time db)
      :http-xhrio {:method :post
                   :uri (kf/path-for [:api/events])

                    ; Update the events for posting; remove the original log
                    ; timestamp and, if the event is a reposting attempt, add
                    ; the millisecond delay between the original log timestamp
                    ; and the posting timestamp (we do no adjustment of the
                    ; delay here as the value will be clamped server-side).
                   :params (map (fn [event]
                                  (cond-> event
                                    is-retry? (assoc-in [:data :delay] (- now-ts
                                                                          (get-in event
                                                                                  [:data :log-ts]
                                                                                  0)))
                                    :always (update-in [:data] dissoc :log-ts :created)))
                                events)

                   :format (edn-request-format)
                   :response-format (edn-response-format)
                   :on-success [::post-events-success request]
                   :on-failure [::post-events-failure request]}})))

(rf/reg-event-fx
 ::post-events-success
 (fn post-events-success
   [{:keys [db]} [_ {:keys [on-success]} _]]
   (cond-> {:db (session-db/reset-events-retries db)}
     on-success (assoc :dispatch on-success))))

(rf/reg-event-fx
 ::post-events-failure
 (fn post-events-failure
   [{:keys [db]} [_ {:keys [events on-failure] :as request} response]]
   (let [db (session-db/post-events-failure db)
         retry-events? (session-db/retry-post-events? db)
         retry-backoff-millis (backoff-millis db)]
     (when-not retry-events?
       (log/error "Failed to post session events" {:events events
                                                   :response response}))
     (cond-> {:db db}

        ; Retry sending the request after a backoff period.
       retry-events? (assoc :dispatch-later
                            {:ms retry-backoff-millis
                             :dispatch [::post-events request true]})

       (not retry-events?) (update :db session-db/clear-last-events-time)
       (and (not retry-events?) on-failure) (assoc :dispatch on-failure)))))

;;; Higher-level

(rf/reg-event-fx
 ::log-events
 [(rf/inject-cofx :cookie/get [:auth-token])]
 (fn log-events
   [{{:keys [auth-token]} :cookie/get :keys [db]} [_ request]]
   (if-let [user-id (or (user-db/get-user-id db) (:id (:sub (token/peek auth-token))))]
     (let [log-ts (->millis-since-epoch (t/now))]

        ; Update the events with the user identifier and the client-side log
        ; timestamp (milliseconds since the epoch).
       {:dispatch [::post-events (update request
                                         :events (partial mapv #(-> %
                                                                    (assoc :user-id user-id)
                                                                    (assoc-in [:data :log-ts] log-ts))))]})

     {:dispatch [::sentry/capture-message
                 {:message "user-id not present in log-event handler"
                  :data {:request request}}]})))

(rf/reg-event-fx
 ::maybe-ping-events
 (fn maybe-ping-events
   [{:keys [db]} [_ request]]
   (when (session-db/ping-interval-has-passed-since-last-events? db)
     {:db (session-db/set-last-events-time db)
      :dispatch [::log-events request]})))
